import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import type { PlatformUserRole } from "@/types";

interface RoleProtectedRouteProps {
  /**
   * Required role (with hierarchy check)
   */
  requiredRole?: PlatformUserRole;
  /** Exact roles allowed to enter this route. */
  allowedRoles?: PlatformUserRole[];
  /**
   * Redirect path when access denied
   * @default "/forbidden"
   */
  redirectTo?: string;
  /**
   * Children to render
   */
  children: ReactNode;
}

/**
 * Route guard for platform role-based access in admin portal
 *
 * @example
 * <Route
 *   path="/governance/roles"
 *   element={
 *     <RoleProtectedRoute requiredRole="SUPER_ADMIN">
 *       <RoleManagementPage />
 *     </RoleProtectedRoute>
 *   }
 * />
 */
export function RoleProtectedRoute({
  requiredRole,
  allowedRoles,
  redirectTo = "/forbidden",
  children,
}: RoleProtectedRouteProps) {
  const location = useLocation();
  const { hasRole, user } = usePermissions();

  // If user is not loaded yet, show loading
  if (!user) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-gray-500 text-sm">Loading...</div>
      </div>
    );
  }

  const hasAccess = allowedRoles
    ? Boolean(user.role && allowedRoles.includes(user.role))
    : requiredRole
      ? hasRole(requiredRole)
      : true;

  if (!hasAccess) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
